<template>
  <public-layout class="page-home">
    <section class="section hero">
      <div class="container">
        <div class="columns">
          <div class="column is-5">
            <h1 class="claim">The Home of Fun Learning</h1>
            <h2 class="description">Discover holistic early education solutions, easy-to-implement resources and innovative digital tools.<br>Curated for you by Fun Learning pioneers at <a href="https://funacademy.fi/" target="_blank">Fun Academy</a>.</h2>
            <Button :to="{ name: 'login', params: { redirect: $route.fullPath } }" size="large" v-if="!isAuthenticated">Get started for free</Button>
          </div>
          <div class="column is-7">
            <img src="/static/img/assets/home.jpg" alt="">
          </div>
        </div>
      </div>
    </section>

    <section class="section features">
      <div class="container">
        <h2 class="section-title">We are your <strong>digital teacher's assistant</strong></h2>
        <ul class="feature-list">
          <li class="feature">
            <img class="feature-icon" src="/static/img/B1.svg" alt="">
            <p class="feature-text">Solutions based on the innovative Fun Learning approach</p>
          </li>
          <li class="feature">
            <img class="feature-icon" src="/static/img/B2.svg" alt="">
            <p class="feature-text">Inspiring content curated by pedagogical experts</p>
          </li>
          <li class="feature">
            <img class="feature-icon" src="/static/img/B3.svg" alt="">
            <p class="feature-text">Rooted in the world-renowned Finnish education expertise</p>
          </li>
          <li class="feature">
            <img class="feature-icon" src="/static/img/B4.svg" alt="">
            <p class="feature-text">Universally adaptable to fit local cultures and curricular goals</p>
          </li>
        </ul>
        <Button :to="{ name: 'about-us' }" size="medium">Read our full story</Button>
      </div>
    </section>

    <section class="section services is-light">
      <div class="container">
        <h2 class="section-title">Your inspiring <strong>education resource hub</strong></h2>
        <p class="section-subtitle">Based on the Fun Learning approach to give every child everywhere the best start in life!</p>

        <flickity ref="flickity" :options="flickityOptions">
          <div class="carousel-cell" v-for="(service, index) in services" :key="index">
            <ServiceThumbnail :service="service"></ServiceThumbnail>
          </div>
        </flickity>
        <br>
        <!-- <div class="columns service-list is-multiline">
          <div class="column is-4" v-for="(service, index) in services" :key="index">
            <ServiceThumbnail :service="service"></ServiceThumbnail>
          </div>
        </div> -->
      </div>
    </section>

    <section-testimonies></section-testimonies>

    <section class="section subscribe">
      <div class="container has-text-centered">
        <h4 class="section-title">Start your <strong>Fun Learning adventure today!</strong></h4>
        <p class="section-subtitle">Looking to step into the future of Early Childhood Education? Discover the solution that’s right for you!</p>
        <Button :to="{ name: 'login', params: { redirect: $route.fullPath } }" size="large" v-if="!isAuthenticated">Get started for free</Button>
      </div>
    </section>
  </public-layout>
</template>

<script>

import PublicLayout from '@/layouts/PublicLayout'

import Flickity from 'vue-flickity'
import Button from '@/components/Button'
import SectionTestimonies from '@/components/SectionTestimonies'
import ServiceThumbnail from '@/components/ServiceThumbnail'

export default {
  name: 'homepage',

  components: {
    PublicLayout,
    Flickity,
    Button,
    SectionTestimonies,
    ServiceThumbnail
  },

  computed: {
    isAuthenticated () {
      return this.$store.state.isAuthenticated
    }
  },

  methods: {
    updateSlidesPerPage () {
      const width = window.innerWidth
      let slidesPerPage = this.flickityOptions.groupCells

      if (width >= 1024) {
        slidesPerPage = 3
      } else if (width >= 640) {
        slidesPerPage = 2
      } else if (width < 640) {
        slidesPerPage = 1
      }

      if (slidesPerPage !== this.flickityOptions.groupCells) {
        this.flickityOptions.groupCells = slidesPerPage
      }
    },

    resizeSlider () {
      this.updateSlidesPerPage()
      this.$refs.flickity.resize()
    }
  },

  mounted () {
    this.resizeSlider()
  },

  created () {
    this.updateSlidesPerPage()
    window.addEventListener('resize', this.resizeSlider)
  },

  data () {
    return {
      flickityOptions: {
        adaptiveHeight: true,
        initialIndex: 0,
        groupCells: 1,
        contain: true
      },
      services: [
        {
          id: 1,
          to: 'resources',
          title: 'Free Resources',
          thumb: '/static/img/assets/home-1.png',
          description: 'The innovative educational resources shelf. Created and curated by pedagogical experts. Designed to spark curiosity and instil a passion for learning in preschoolers.'
        },
        {
          id: 2,
          to: 'future-explorers',
          title: 'Programs',
          thumb: '/static/img/assets/home-2.png',
          description: 'Programs designed to encourage children to explore professional aspirations, discover core strengths and develop life skills necessary for the future.'
        },
        {
          id: 3,
          to: 'prof-dev',
          title: 'Professional Development',
          thumb: '/static/img/assets/home-5.png',
          description: 'Continuous professional development for teachers, uniquely designed to implement today’s best practices in the classroom. Includes onsite and online training.'
        },
        {
          id: 4,
          to: 'digital-tools',
          title: 'Camps',
          thumb: '/static/img/assets/home-3.png',
          description: 'International Fun Learning exchanges for preschoolers and their parents. Visit Finland. Enroll in a local kindergarten. Experience Finnish culture and education excellence first hand.'
        },
        {
          id: 5,
          to: 'kinder',
          title: 'Kindergarten',
          thumb: '/static/img/assets/home-4.png',
          description: 'A comprehensive package with cutting-edge pedagogical tools and material, top quality professional development and inspiring learning environments.'
        }
      ]
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">

@import '../../node_modules/bulma/sass/utilities/initial-variables';
@import '~styles/base/variables';
@import '~styles/base/mixins';

.page-home {

  .hero {
    .columns {
      flex-direction: row-reverse;
      align-items: center
    }

    .claim {
      margin-bottom: 1rem;
      @include text-size-xxxl();
      color: $color-brand-main;
      font-weight: bold;
    }

    .description {
      margin-bottom: 2rem;
      font-weight: lighter;
      color: #6B7285;
    }
  }

  .service-list {

    p {
      margin-bottom: 10px;
    }

    a {
      display: inline-block;
      color: #E31C79;
      margin-bottom: 30px;
    }
  }

  .features {

    .feature-list {
      display: flex;
      flex-wrap: wrap;
      margin-bottom: 2rem;
    }

    .feature {
      flex: 0 0 100%;
      margin: 2rem 0 1rem;
      padding: 0 0.5rem;

      @media screen and (min-width: $tablet) {
        flex-basis: 50%;
      }

      @media screen and (min-width: $desktop) {
        flex-basis: 25%;
      }
    }

    .feature-icon {
      width: 5.5rem;
      height: 5.5rem;
      margin-bottom: 1rem;
    }
  }

  .services {

    .service-list {
      overflow-x: auto;
    }
  }

  .subscribe {
    height: 34rem;
    background-image: url('/static/img/home-subscribe-bg.svg');
    background-position-x: center;
    background-position-y: bottom;
    background-repeat: no-repeat;
    background-size: contain;

    @media screen and (min-width: $desktop) {
      background-size: 60%;
    }
  }

  // Adjustments for slider elements
  .flickity-enabled {
    margin-left: 1.5rem;
    margin-right: 1.5rem;
  }

  .carousel-cell {
    width: 100%;
    padding: .75rem;

    @media screen and (min-width: 640px) {
      width: 50%;
    }

    @media screen and (min-width: 1024px) {
      width: 33%;
    }
  }

  .flickity-prev-next-button.previous { left: -40px; }
  .flickity-prev-next-button.next { right: -40px; }
}

</style>



// WEBPACK FOOTER //
// src/pages/HomePage.vue